import { useCallback, useEffect, useRef } from 'react';

export interface GamepadRef {
  [key: number]: Gamepad;
}

export function useGamepads(callback: (data: GamepadRef) => void, frequency: number) {
  const gamepads = useRef<GamepadRef>({});
  const intervalRef = useRef<number | null>(null);

  const addGamepad = useCallback(
    (gamepad: Gamepad) => {
      gamepads.current = {
        ...gamepads.current,
        [gamepad.index]: gamepad,
      };
      // Send data to external callback (like React state)
      callback(gamepads.current);
    },
    [callback]
  );

  useEffect(() => {
    const connectGamepadHandler = (e: Event) => {
      addGamepad((e as GamepadEvent).gamepad);
    };
    window.addEventListener('gamepadconnected', connectGamepadHandler);
    return () => {
      window.removeEventListener('gamepadconnected', connectGamepadHandler);
    };
  }, [addGamepad]);

  useEffect(() => {
    const scanGamepads = () => {
      const detectedGamepads = navigator.getGamepads ? navigator.getGamepads() : [];
      for (let i = 0; i < detectedGamepads.length; i++) {
        const newGamepad = detectedGamepads[i];
        if (newGamepad) {
          addGamepad(newGamepad);
        }
      }
    };
    const updateStatus = () => {
      scanGamepads();
    };
    intervalRef.current = window.setInterval(updateStatus, frequency);
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, [addGamepad, frequency]);

  return gamepads.current;
}
